import { Box, Typography } from "@mui/material";
import React from "react";
import { Cell, Pie, PieChart, PieLabelRenderProps, Tooltip } from "recharts";
import { ChartData, chartColors } from "../Dashboard";
import { isEmpty, isNil } from "ramda";
import ProgressIndicator from "../../../common/progressIndicator/ProgressIndicator";

interface PieChartProps {
  data: ChartData[];
  chartWidth: number;
  chartHeight: number;
  loading: boolean;
}

const classes = {
  chartText: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginRight: "5px",
  },
};

const PieChartComponent = (props: PieChartProps) => {
  const { data, chartWidth, chartHeight, loading } = props;

  const CustomLabel: React.FC<PieLabelRenderProps> = (props) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, percent = 0 } = props;
    const RADIAN = Math.PI / 180;
    const radius =
      Number(innerRadius) + (Number(outerRadius) - Number(innerRadius)) * 0.5;
    const x = Number(cx) + radius * Math.cos(-midAngle * RADIAN);
    const y = Number(cy) + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > Number(cx) ? "start" : "middle"}
        dominantBaseline="central"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <>
      {loading || isNil(data) ? (
        <Box
          display="flex"
          justifyContent="center"
          flexDirection="column"
          padding={2}
          minHeight={chartHeight}
        >
          <ProgressIndicator noPadding />
        </Box>
      ) : isEmpty(data) ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={chartHeight}
        >
          No data available
        </Box>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          flexDirection="column"
          padding={2}
        >
          <Box width="100%" display="flex" justifyContent="center">
            <PieChart width={chartWidth} height={chartHeight}>
              <Pie
                nameKey="name"
                dataKey="value"
                isAnimationActive
                labelLine={false}
                data={data}
                cx="50%"
                cy="45%"
                innerRadius={40}
                outerRadius={100}
                label={CustomLabel}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={chartColors[index % chartColors.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                cursor={{ fill: "rgba(239, 239, 239, 0.3)" }}
                contentStyle={{
                  backgroundColor: "#fff",
                  borderRadius: "5px",
                  borderColor: "#efefef",
                  borderWidth: "1px",
                  fontSize: "13px",
                }}
                labelStyle={{ color: "#555", fontWeight: 600 }}
                formatter={(value: unknown, name: string) => {
                  return [`${name}: ${value}`];
                }}
              />
            </PieChart>
          </Box>

          <Box width="100%">
            {data.map((item, idx) => (
              <Box key={idx} display="flex" alignItems="center">
                <Box
                  sx={classes.chartText}
                  style={{
                    backgroundColor: chartColors[idx % chartColors.length],
                  }}
                ></Box>

                <Typography>{item.name}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </>
  );
};

export default PieChartComponent;
